import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { z } from "zod";
import { AppShell } from "@/components/AppShell";
import { TermsContent } from "@/routes/terms";
import { FeesContent } from "@/routes/fees";
import { SellerPolicyContent } from "@/routes/seller-policy";
import { RatingPolicyContent } from "@/routes/rating-policy";
import { PlatformPoliciesContent } from "@/routes/platform-policies";
import { ConditionGuideContent } from "@/routes/condition-guide";

const TABS = [
  { id: "terms", label: "Terms & Conditions" },
  { id: "platform", label: "Platform Policies" },
  { id: "fees", label: "Fees" },
  { id: "seller", label: "Seller Policy" },
  { id: "rating", label: "Rating & Strike Policy" },
  { id: "condition", label: "Condition Guide" },
] as const;

type TabId = (typeof TABS)[number]["id"];

const searchSchema = z.object({
  tab: z.enum(["terms", "platform", "fees", "seller", "rating", "condition"]).optional(),
});

export const Route = createFileRoute("/policies")({
  validateSearch: (s) => searchSchema.parse(s),
  head: () => ({
    meta: [
      { title: "Policies — Want To Buy" },
      {
        name: "description",
        content:
          "Want To Buy platform policies: Terms & Conditions, Platform Policies, Fees, Seller Policy, and Rating & Strike Policy — all in one place.",
      },
    ],
  }),
  component: PoliciesPage,
});

function PoliciesPage() {
  const { tab } = Route.useSearch();
  const active: TabId = tab ?? "terms";
  const meta = useMemo(() => TABS.find((t) => t.id === active)!, [active]);

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl">
        <header className="mb-6">
          <div className="text-xs font-semibold uppercase tracking-wider text-primary">Legal & Trust</div>
          <h1 className="mt-1 font-display text-4xl font-black tracking-tight">Policies</h1>
          <p className="mt-3 max-w-3xl text-base text-muted-foreground">
            Everything that governs how Want To Buy works — payments, seller obligations, ratings, and bans — in one place.
          </p>
        </header>

        <div className="grid gap-6 lg:grid-cols-[220px_1fr]">
          <nav className="lg:sticky lg:top-24 lg:self-start">
            <ul className="flex gap-1 overflow-x-auto rounded-md border border-border bg-panel p-1 lg:flex-col lg:overflow-visible lg:p-2">
              {TABS.map((t) => {
                const isActive = t.id === active;
                return (
                  <li key={t.id} className="lg:w-full">
                    <Link
                      to="/policies"
                      search={{ tab: t.id }}
                      replace
                      className={`block whitespace-nowrap rounded px-3 py-2 text-sm font-semibold transition ${
                        isActive
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      }`}
                    >
                      {t.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          <article>
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{meta.label}</div>
            <div className="prose-doc mt-3 space-y-6">
              {active === "terms" && <TermsContent />}
              {active === "platform" && <PlatformPoliciesContent />}
              {active === "fees" && <FeesContent />}
              {active === "seller" && <SellerPolicyContent />}
              {active === "rating" && <RatingPolicyContent />}
              {active === "condition" && <ConditionGuideContent />}
            </div>
          </article>
        </div>
      </div>

      <style>{`
        .prose-doc h2 { font-family: var(--font-display, "Space Grotesk", system-ui); font-size: 1.4rem; font-weight: 700; margin-top: 1.5rem; }
        .prose-doc h3 { font-size: 1.05rem; font-weight: 700; margin-top: 1rem; }
        .prose-doc p, .prose-doc li { color: color-mix(in oklab, var(--color-foreground) 80%, transparent); line-height: 1.65; }
        .prose-doc ul { list-style: disc; padding-left: 1.25rem; }
        .prose-doc ol { list-style: decimal; padding-left: 1.25rem; }
        .prose-doc a { color: var(--color-primary); text-decoration: underline; text-underline-offset: 2px; }
        .prose-doc .panel-note { border: 1px solid var(--color-border); background: color-mix(in oklab, var(--color-panel) 60%, transparent); padding: 1rem 1.25rem; border-radius: 0.5rem; }
      `}</style>
    </AppShell>
  );
}
